import { useState, useEffect, useRef, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import { MapPin, Package, Search } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ProductGridSkeleton from '../components/ProductGridSkeleton';
import ProductResultsToolbar from '../components/ProductResultsToolbar';
import { searchProducts, getPublicVersions } from '../services/api.shop';
import { useFrontendAuth } from '../hooks/useAuth';
import { Product, PaginatedResponse } from '../types';
import BackToHomeLink from '../components/BackToHomeLink';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import {
    DEFAULT_PRODUCT_SORT,
    ProductSortKey,
    productSortApiValue,
    productSortKeyFromParam,
} from '../utils/productSort';

/** Age range from product specs for card meta chips (optional). */
function ageChip(product: Product): string | null {
    const specs = product.specifications || {};
    for (const cat of Object.values(specs)) {
        for (const [name, value] of Object.entries(cat)) {
            if (/age/i.test(name) && value) return value;
        }
    }
    return null;
}

/**
 * Merged SearchResults:
 * - StartToBaby: BackToHomeLink + public product version cache invalidation
 * - #197: location filter, q+location search, i18n title/count, empty states,
 *         age/location chips, mobile pagination polish
 */
const SearchResults = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    const locationParam = searchParams.get('location') || '';
    const sortParam = searchParams.get('sort');
    const initialPage = parseInt(searchParams.get('page') || '1', 10);
    const { user } = useFrontendAuth();
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [locationInput, setLocationInput] = useState(locationParam);
    const sortKey = productSortKeyFromParam(sortParam) || DEFAULT_PRODUCT_SORT;
    const sortApi = productSortApiValue(sortKey);
    const limit = 20;
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);

    useEffect(() => {
        setLocationInput(locationParam);
    }, [locationParam]);

    useEffect(() => {
        setCurrentPage(initialPage);
    }, [query, locationParam, sortParam, initialPage]);

    const enabled = !!(query.trim() || locationParam.trim());

    const excludeSellerId =
        user?.excludeOwnProducts && user.id ? user.id : undefined;

    // Sort + viewer id in queryKey so reservedByMe never leaks across accounts
    const viewerKey = user?.id ?? 'anon';
    const { data, isLoading: loading, isFetching, error } = useQuery<PaginatedResponse<Product>>({
        queryKey: ['search', { q: query, location: locationParam, page: currentPage, filter: sortApi, excludeSellerId, viewerKey }],
        queryFn: () =>
            searchProducts({
                q: query || undefined,
                location: locationParam || undefined,
                page: currentPage,
                limit,
                filter: sortApi,
                excludeSellerId,
            }),
        enabled,
        placeholderData: keepPreviousData,
        // Keep short so post-edit price changes surface quickly even if invalidation is missed
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: 'always',
    });

    const queryClient = useQueryClient();

    const { data: versions } = useQuery({
        queryKey: ['publicVersions'],
        queryFn: getPublicVersions,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
        refetchOnWindowFocus: true,
    });

    const prevSearchVersion = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (
            versions?.products &&
            prevSearchVersion.current !== undefined &&
            prevSearchVersion.current !== versions.products
        ) {
            queryClient.invalidateQueries({ queryKey: ['search'] });
        }
        prevSearchVersion.current = versions?.products;
    }, [versions?.products, queryClient]);

    const products: Product[] = data?.data && Array.isArray(data.data) ? data.data : [];
    const total = data?.total ?? 0;
    const totalPages = total ? Math.ceil(total / limit) : 1;

    const buildParams = (overrides: Record<string, string | undefined> = {}) => {
        const next: Record<string, string> = {};
        const q = overrides.q !== undefined ? overrides.q : query;
        const location = overrides.location !== undefined ? overrides.location : locationParam;
        const sort = overrides.sort !== undefined ? overrides.sort : sortKey;
        const page = overrides.page !== undefined ? overrides.page : String(currentPage);
        if (q?.trim()) next.q = q.trim();
        if (location?.trim()) next.location = location.trim();
        if (sort && sort !== DEFAULT_PRODUCT_SORT) next.sort = sort;
        if (page && page !== '1') next.page = page;
        return next;
    };

    const applyLocation = (e: FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        setSearchParams(
            buildParams({ location: locationInput, page: '1' })
        );
    };

    const clearLocation = () => {
        setLocationInput('');
        setCurrentPage(1);
        setSearchParams(buildParams({ location: '', page: '1' }));
    };

    const handleSortChange = (key: ProductSortKey) => {
        setCurrentPage(1);
        setSearchParams(buildParams({ sort: key, page: '1' }));
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            setSearchParams(buildParams({ page: String(newPage) }));
        }
    };

    const titleBits: string[] = [];
    if (query.trim()) titleBits.push(`“${query.trim()}”`);
    if (locationParam.trim()) titleBits.push(locationParam.trim());

    return (
        <div className="mx-auto w-full max-w-7xl min-w-0">
            <BackToHomeLink />

            <header className="mb-4 sm:mb-6">
                <h1 className="break-words text-xl font-bold text-[var(--text-color)] sm:text-2xl lg:text-3xl">
                    {titleBits.length > 0
                        ? tr('search.results.title', 'Results for {terms}').replace(
                              '{terms}',
                              titleBits.join(' · ')
                          )
                        : tr('search.results.browse', 'Search the marketplace')}
                </h1>
            </header>

            {/* Location filter: label → input+actions row → privacy note */}
            <form
                onSubmit={applyLocation}
                className="mb-6 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-3 sm:p-4"
            >
                <label
                    htmlFor="search-location"
                    className="mb-2 flex items-center gap-1.5 text-xs font-medium text-[var(--text-color)]"
                >
                    <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span className="leading-snug">
                        {tr('search.location.label', 'Location (city, region or province)')}
                    </span>
                </label>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                    <input
                        id="search-location"
                        type="text"
                        value={locationInput}
                        onChange={(e) => setLocationInput(e.target.value)}
                        placeholder={tr('search.location.placeholder', 'e.g. Antwerp, Flanders…')}
                        enterKeyHint="search"
                        autoComplete="address-level2"
                        className="min-h-11 min-w-0 flex-1 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] bg-[var(--bg-color)] px-3 py-2.5 text-base text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]/40 sm:text-sm"
                    />
                    <div className="flex shrink-0 gap-2">
                        <button
                            type="submit"
                            className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[var(--accent-color)] px-5 text-sm font-semibold text-white hover:opacity-90 sm:flex-none"
                        >
                            <Search className="h-4 w-4 shrink-0" aria-hidden />
                            {tr('search.location.apply', 'Apply')}
                        </button>
                        {locationParam && (
                            <button
                                type="button"
                                onClick={clearLocation}
                                className="min-h-11 flex-1 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] px-4 text-sm text-[var(--text-color)] sm:flex-none"
                            >
                                {tr('search.location.clear', 'Clear')}
                            </button>
                        )}
                    </div>
                </div>

                <p className="mt-2 text-[11px] leading-snug text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                    {tr(
                        'search.location.privacy',
                        'Only city and region are used — never full street addresses.'
                    )}
                </p>
            </form>

            {!enabled ? (
                <div className="rounded-2xl border border-dashed border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] px-6 py-14 text-center">
                    <Search className="mx-auto mb-3 h-10 w-10 opacity-35" aria-hidden />
                    <p className="text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                        {tr('search.results.hint', 'Type a search above or filter by location.')}
                    </p>
                    <Link to="/home" className="mt-4 inline-block text-sm font-semibold underline">
                        {tr('search.results.home', 'Back to home')}
                    </Link>
                </div>
            ) : loading ? (
                <ProductGridSkeleton count={8} />
            ) : error ? (
                <div className="py-8 text-center text-red-600">
                    {tr('search.results.error', 'Failed to load results')}
                </div>
            ) : (
                <>
                    <ProductResultsToolbar
                        count={total}
                        sort={sortKey}
                        onSortChange={handleSortChange}
                        showCount={!isFetching || total > 0}
                    />
                    {isFetching && !loading ? (
                        <p className="mb-2 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                            …
                        </p>
                    ) : null}
                    {products.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] px-6 py-14 text-center">
                            <Package className="mx-auto mb-3 h-10 w-10 opacity-35" aria-hidden />
                            <p className="text-lg text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                {t('search.products.nonefound') || 'No products found'}
                            </p>
                            <p className="mt-2 text-sm opacity-70">
                                {tr('search.results.try', 'Try another word or a broader location.')}
                            </p>
                            <Link
                                to="/home"
                                className="mt-4 inline-flex text-sm font-semibold text-[var(--text-color)] underline"
                            >
                                {tr('search.results.home', 'Back to home')}
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="product-grid">
                                {products.map((product) => {
                                    const age = ageChip(product);
                                    // Location already on ProductCard header (sellerRegion). Only chip city
                                    // when it adds info beyond that (avoids double "Kortrijk").
                                    const city = (product.sellerCity || '').trim();
                                    const region = (product.sellerRegion || '').trim();
                                    const placeChip =
                                        city &&
                                        (!region || city.toLowerCase() !== region.toLowerCase())
                                            ? city
                                            : null;
                                    return (
                                        <div key={product.id} className="flex min-w-0 flex-col">
                                            <ProductCard
                                                product={product}
                                                showPrice={!!user}
                                                hideOwnBadge={false}
                                            />
                                            {(age || placeChip) && (
                                                <div className="mt-1.5 flex flex-wrap gap-1.5 px-0.5">
                                                    {age && (
                                                        <span className="rounded-full bg-[color-mix(in_srgb,var(--accent-color),transparent_70%)] px-2.5 py-1 text-[11px] font-semibold text-[var(--text-color)]">
                                                            {age}
                                                        </span>
                                                    )}
                                                    {placeChip && (
                                                        <span className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-[color-mix(in_srgb,var(--accent-color),transparent_80%)] px-2.5 py-1 text-[11px] font-medium text-[var(--text-color)]">
                                                            <MapPin className="h-3 w-3 shrink-0" aria-hidden />
                                                            <span className="truncate">{placeChip}</span>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {totalPages > 1 && (
                                <div className="mt-8 mb-2 flex flex-wrap items-center justify-center gap-2 sm:gap-4">
                                    <button
                                        type="button"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="min-h-11 min-w-[7rem] rounded-xl bg-[var(--accent-color)] px-4 py-2 text-sm text-white disabled:opacity-50 hover:opacity-90"
                                    >
                                        {tr('search.prev', 'Previous')}
                                    </button>
                                    <span className="px-2 text-sm text-[var(--text-color)]">
                                        {t('search.page') || 'Page'} {currentPage} / {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="min-h-11 min-w-[7rem] rounded-xl bg-[var(--accent-color)] px-4 py-2 text-sm text-white disabled:opacity-50 hover:opacity-90"
                                    >
                                        {tr('search.next', 'Next')}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default SearchResults;
